import type { RefObject } from 'preact';
import { useCallback, useLayoutEffect } from 'preact/hooks';

export const autoFocusInsideProps = {
  'data-autofocus-inside': true,
} as const;

export const focusGuardProps = {
  'data-focus-guard': true,
  'tabIndex': 0,
} as const;

export function useAutoFocus(
  rootRef: RefObject<HTMLElement>,
  inputRef: RefObject<HTMLInputElement>,
  enabled: boolean,
): () => boolean {
  const focusInside = useCallback(() => {
    if (!enabled) {
      return false;
    }

    const target = inputRef.current;

    if (!target) {
      return false;
    }

    if (target.ownerDocument.activeElement !== target) {
      target.focus();
    }

    return target.ownerDocument.activeElement === target;
  }, [enabled, inputRef]);

  useLayoutEffect(() => {
    const root = rootRef.current;

    if (!enabled || !root) {
      return undefined;
    }

    const { ownerDocument } = root;
    let lastFocusedInput: HTMLInputElement | null = null;
    let restoringFocus = false;
    let timeoutId: number | undefined;

    const restoreFocus = (): boolean => {
      if (restoringFocus || !root.isConnected) {
        return false;
      }

      const { activeElement } = ownerDocument;

      if (activeElement && root.contains(activeElement)) {
        if (activeElement === inputRef.current) {
          lastFocusedInput = inputRef.current;
        }

        return true;
      }

      if (
        (!activeElement ||
          activeElement === ownerDocument.body ||
          activeElement === ownerDocument.documentElement) &&
        lastFocusedInput &&
        lastFocusedInput === inputRef.current &&
        lastFocusedInput.isConnected
      ) {
        return false;
      }

      restoringFocus = true;

      try {
        const focused = focusInside();

        if (focused) {
          lastFocusedInput = inputRef.current;
        }

        return focused;
      } finally {
        restoringFocus = false;
      }
    };

    const onFocusOut = () => {
      if (restoringFocus) {
        return;
      }

      window.clearTimeout(timeoutId);
      timeoutId = window.setTimeout(() => {
        timeoutId = undefined;
        restoreFocus();
      }, 1);
    };

    ownerDocument.addEventListener('focusin', restoreFocus);
    ownerDocument.addEventListener('focusout', onFocusOut);

    restoreFocus();
    const initialFocusTimeoutId = window.setTimeout(restoreFocus, 1);

    return () => {
      ownerDocument.removeEventListener('focusin', restoreFocus);
      ownerDocument.removeEventListener('focusout', onFocusOut);
      window.clearTimeout(initialFocusTimeoutId);
      window.clearTimeout(timeoutId);
    };
  }, [enabled, focusInside, inputRef, rootRef]);

  return focusInside;
}
